import { HIApi } from '@/entities/monitoring/hi';
import { RulesAccess } from '@/entities/user/access-rule';
import { ACCESS_RULES_TREE } from '@/entities/user/access-rule';
import { HI_STATUS, StatusDot } from '@/entities/monitoring/hi';
import { useNavigateToCIHealth } from '@/features/srm-view';
import { HIActiveHighLevelCI } from '@/entities/monitoring/hi';
import { getFormattedDurationFromValue } from '@/shared/lib/utils/date-time';
import { getHIColourByStatus } from '@/entities/monitoring/hi';
import { getNextPageParamFn } from '@/shared/lib/utils/pagination/get-next-page-param';
import {
  ActionIcon,
  Badge,
  Box,
  Center,
  Divider,
  Group,
  Loader,
  NavLink,
  Popover,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import IconUrgent from '@tabler/icons-react/dist/esm/icons/IconUrgent.mjs';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const NOTIFICATION_SEVERITIES = [
  HI_STATUS.WARNING,
  HI_STATUS.ERROR,
  HI_STATUS.CRITICAL_ERROR,
];

const NOTIFICATIONS_PAGE_SIZE = 20;
const NOTIFICATION_ROW_HEIGHT = 81;

interface HINotificationsListProps {
  items: HIActiveHighLevelCI[];
  scrollElement: HTMLElement | null;
  onSelect: (item: HIActiveHighLevelCI) => void;
}

const HINotificationsList = ({
  items,
  scrollElement,
  onSelect,
}: HINotificationsListProps) => {
  const { t } = useTranslation();
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => NOTIFICATION_ROW_HEIGHT,
    initialRect: { width: 500, height: 360 },
    overscan: 3,
  });

  return (
    <Box
      pos='relative'
      h={rowVirtualizer.getTotalSize()}
      data-testid='notifications-hi-list'
    >
      {rowVirtualizer.getVirtualItems().map((virtualItem) => {
        const item = items[virtualItem.index];
        const duration = getFormattedDurationFromValue(
          item.worst_duration_sec,
          t,
          'minute',
          'second',
        );
        const additionalCount = Math.max(0, item.his_count - 1);

        return (
          <Box
            key={item.hi_id}
            pos='absolute'
            top={0}
            left={0}
            w='100%'
            ref={rowVirtualizer.measureElement}
            data-index={virtualItem.index}
            style={{ transform: `translateY(${virtualItem.start}px)` }}
          >
            {virtualItem.index > 0 ? <Divider /> : null}
            <NavLink
              p='md'
              onClick={() => onSelect(item)}
              data-testid={`notifications-hi-row-${item.hi_id}`}
              leftSection={<StatusDot severity={item.worst_severity} />}
              label={
                <Text
                  fw={600}
                  truncate
                  data-testid={`notifications-hi-ci-${item.hi_id}`}
                >
                  {item.ci_name}
                </Text>
              }
              description={
                <Group gap='xs' wrap='nowrap'>
                  <Text c='dimmed' size='sm' truncate>
                    {item.hi_type_label ?? item.hi_type_name}
                  </Text>
                  {additionalCount > 0 ? (
                    <Badge
                      size='sm'
                      variant='light'
                      data-testid={`notifications-hi-additional-${item.hi_id}`}
                    >
                      +{additionalCount}
                    </Badge>
                  ) : null}
                </Group>
              }
              rightSection={
                <Stack gap={2} align='flex-end' miw='max-content'>
                  <Text
                    size='sm'
                    fw={600}
                    c={getHIColourByStatus(item.worst_severity)}
                    data-testid={`notifications-hi-duration-${item.hi_id}`}
                  >
                    {duration || '—'}
                  </Text>
                  <Text size='xs' c='dimmed'>
                    {item.ci_type_label ?? item.ci_type_name}
                  </Text>
                </Stack>
              }
            />
          </Box>
        );
      })}
    </Box>
  );
};

export const HINotifications = () => {
  const { t } = useTranslation();
  const navigateToCIHealth = useNavigateToCIHealth();
  const [opened, setOpened] = useState(false);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);

  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['active-hi-notifications', NOTIFICATION_SEVERITIES],
    enabled: false,
    queryFn: ({ pageParam = 1 }) =>
      HIApi.getActiveHighLevelCI({
        params: {
          page: pageParam,
          size: NOTIFICATIONS_PAGE_SIZE,
          severities: NOTIFICATION_SEVERITIES,
        },
      }).then((response) => response.data),
    getNextPageParam: getNextPageParamFn,
    cacheTime: 0,
  });

  const handleOpenedChange = useCallback(
    (nextOpened: boolean) => {
      setOpened(nextOpened);
      if (nextOpened) void refetch();
    },
    [refetch],
  );

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data?.pages],
  );
  const ciCount = data?.pages[0]?.total ?? items.length;

  const handleHISelect = useCallback(
    (item: HIActiveHighLevelCI) => {
      setOpened(false);
      navigateToCIHealth(item.ci_id, {
        tqlHiLinkId: item.tql_hi_link_id,
        hiTypeName: item.hi_type_name,
        severity: item.worst_severity,
      });
    },
    [navigateToCIHealth],
  );

  const handleOpenCIHealth = useCallback(() => {
    setOpened(false);
    navigateToCIHealth();
  }, [navigateToCIHealth]);

  const content = useMemo(() => {
    if (isLoading && !items.length) {
      return (
        <Center p='xl' data-testid='notifications-hi-loading'>
          <Loader size='sm' />
        </Center>
      );
    }

    if (isError && !items.length) {
      return (
        <Text
          p='md'
          c='dimmed'
          ta='center'
          data-testid='notifications-hi-error'
        >
          {t('hi.popover.loadError', { ns: 'notifications' })}
        </Text>
      );
    }

    if (!items.length) {
      return (
        <Text
          p='md'
          c='dimmed'
          ta='center'
          data-testid='notifications-hi-empty'
        >
          {t('hi.popover.empty', { ns: 'notifications' })}
        </Text>
      );
    }

    return (
      <>
        <HINotificationsList
          items={items}
          scrollElement={scrollElement}
          onSelect={handleHISelect}
        />
        {isFetchingNextPage ? (
          <Center py='sm' data-testid='notifications-hi-loading-more'>
            <Loader size='xs' />
          </Center>
        ) : null}
      </>
    );
  }, [
    isError,
    isFetchingNextPage,
    isLoading,
    items,
    handleHISelect,
    scrollElement,
    t,
  ]);

  return (
    <RulesAccess
      rule={{
        list: [ACCESS_RULES_TREE.dashboards.view, ACCESS_RULES_TREE.srm.view],
      }}
    >
      <Popover
        opened={opened}
        onChange={handleOpenedChange}
        position='bottom-end'
        width={500}
        shadow='md'
      >
        <Popover.Target>
          <Tooltip label={t('hi.title', { ns: 'notifications' })}>
            <ActionIcon
              data-testid='notifications-hi-icon'
              variant='light'
              onClick={() => handleOpenedChange(!opened)}
            >
              <IconUrgent size={20} />
            </ActionIcon>
          </Tooltip>
        </Popover.Target>

        <Popover.Dropdown p={0} data-testid='notifications-hi-popover'>
          <Text fw={600} p='md'>
            {t('hi.title', { ns: 'notifications' })}
          </Text>
          <Divider />

          <ScrollArea.Autosize
            mah={360}
            type='auto'
            data-testid='notifications-hi-scroll'
            viewportRef={setScrollElement}
            viewportProps={{
              'data-testid': 'notifications-hi-scroll-viewport',
            }}
            onBottomReached={
              hasNextPage && !isFetchingNextPage
                ? () => void fetchNextPage()
                : undefined
            }
          >
            {content}
          </ScrollArea.Autosize>

          <Divider />
          <UnstyledButton
            w='100%'
            p='md'
            onClick={handleOpenCIHealth}
            data-testid='notifications-hi-ci-health'
          >
            <Group justify='space-between'>
              <Text size='sm' c='blue'>
                {t('hi.popover.openCiHealth', { ns: 'notifications' })}
              </Text>
              <Text
                size='xs'
                c='dimmed'
                data-testid='notifications-hi-loaded-count'
              >
                {t('hi.popover.loadedCount', {
                  ns: 'notifications',
                  loaded: items.length,
                  total: ciCount,
                })}
              </Text>
            </Group>
          </UnstyledButton>
        </Popover.Dropdown>
      </Popover>
    </RulesAccess>
  );
};